import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { Table } from 'src/design/molecules/Table';
import { formatHours } from 'src/utils';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconExternalLink,
  IconSelector,
} from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DatePeriod } from 'src/routes/Manhours/hooks';
import classes from './ManhoursTable.module.css';

export interface ManhoursTableRow {
  id: string;
  /** Null when the Great Plains job has no linked Firetrol job. */
  jobId: number | null;
  jobNumber: string;
  jobName: string;
  costCode: string;
  ctcHours: number;
  liquidatedDamages: boolean;
  startDate: string | null;
  endDate: string | null;
  weeklyFtes: Record<string, number>;
}

export interface ManhoursTableProps {
  datePeriods: DatePeriod[];
  summary: Record<string, number>;
  rows: ManhoursTableRow[];
  totalCtcHours: number;
}

export const ManhoursTableRowView: React.FC<{
  row: ManhoursTableRow;
  datePeriods: DatePeriod[];
}> = ({ row, datePeriods }) => {
  const navigate = useNavigate();

  const label = (
    <Text c="var(--mantine-color-text)" size="sm" lineClamp={1}>
      {row.jobNumber} - {row.jobName}
    </Text>
  );

  return (
    <Table.Tr
      className={row.liquidatedDamages ? classes.ldRow : undefined}
      data-ld={row.liquidatedDamages ? 'true' : undefined}
    >
      <Table.Td>
        <Group justify="space-between" gap="xs" style={{ width: '400px' }}>
          {row.jobId === null ? (
            label
          ) : (
            <Link to={`/jobs/${row.jobId}/summary`}>{label}</Link>
          )}
          {row.jobId !== null && (
            <BasicActionIcon
              variant="subtle"
              size="sm"
              label="View WBS"
              onClick={() => navigate(`/jobs/${row.jobId}/wbs`)}
            >
              <IconExternalLink size={18} />
            </BasicActionIcon>
          )}
        </Group>
      </Table.Td>
      <Table.Td>{row.liquidatedDamages ? 'Y' : 'N'}</Table.Td>
      <Table.Td
        style={{
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '170px',
        }}
      >
        {row.costCode}
      </Table.Td>
      <Table.Td
        style={{
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '120px',
        }}
      >
        {row.ctcHours.toLocaleString()}
      </Table.Td>
      <Table.Td
        style={{
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '120px',
        }}
      >
        {row.startDate ? (
          row.startDate
        ) : (
          <IconAlertCircle size={18} style={{ color: '#8c8c8c' }} />
        )}
      </Table.Td>
      <Table.Td
        style={{
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '120px',
          borderRight: '1px solid #e9e9e9',
        }}
      >
        {row.endDate ? (
          row.endDate
        ) : (
          <IconAlertCircle size={18} style={{ color: '#8c8c8c' }} />
        )}
      </Table.Td>
      {datePeriods.map((d) => (
        <Table.Td key={d.id} style={{ width: '50px' }}>
          {row.weeklyFtes[d.id] ? row.weeklyFtes[d.id].toFixed(2) : '-'}
        </Table.Td>
      ))}
    </Table.Tr>
  );
};

export const ManhoursTable: React.FC<ManhoursTableProps> = ({
  datePeriods,
  summary,
  rows,
  totalCtcHours,
}) => {
  // We use custom sorting here instead of an existing library like mantine-react-table
  // because we have highly dynamic columns.

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return <IconSelector size={14} />;
    return sortDirection === 'asc' ? (
      <IconChevronUp size={14} />
    ) : (
      <IconChevronDown size={14} />
    );
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;

    return [...rows].sort((a, b) => {
      let aValue: unknown;
      let bValue: unknown;

      if (sortColumn.startsWith('weeklyFtes.')) {
        const periodId = sortColumn.split('.')[1];
        aValue = a.weeklyFtes[periodId] || 0;
        bValue = b.weeklyFtes[periodId] || 0;
      } else {
        aValue = a[sortColumn as keyof ManhoursTableRow];
        bValue = b[sortColumn as keyof ManhoursTableRow];
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const diff = Number(bValue) - Number(aValue);
        return sortDirection === 'asc' ? diff : -diff;
      }

      return 0;
    });
  }, [rows, sortColumn, sortDirection]);

  return (
    <Paper mt="xl">
      <Table.ScrollContainer minWidth={400}>
        <Table stickyHeader striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th
                onClick={() => handleSort('jobNumber')}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs" wrap="nowrap">
                  Project Name
                  {getSortIcon('jobNumber')}
                </Group>
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('liquidatedDamages')}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs" wrap="nowrap">
                  LDS
                  {getSortIcon('liquidatedDamages')}
                </Group>
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('costCode')}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs" wrap="nowrap">
                  Code
                  {getSortIcon('costCode')}
                </Group>
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('ctcHours')}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs" wrap="nowrap">
                  Hours
                  {getSortIcon('ctcHours')}
                </Group>
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('startDate')}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs" wrap="nowrap">
                  Start
                  {getSortIcon('startDate')}
                </Group>
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('endDate')}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs" wrap="nowrap">
                  Finish
                  {getSortIcon('endDate')}
                </Group>
              </Table.Th>
              {datePeriods.map((d) => (
                <Table.Th
                  key={d.id}
                  onClick={() => handleSort(`weeklyFtes.${d.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <Group gap="xs" wrap="nowrap">
                    {d.label}
                    {getSortIcon(`weeklyFtes.${d.id}`)}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr style={{ backgroundColor: '#e7e7e7' }}>
              <Table.Td>Total</Table.Td>
              <Table.Td colSpan={5} />
              {datePeriods.map(({ id }) => {
                const value = summary[id];
                if (!value || value === 0) {
                  return <Table.Td key={id} />;
                }
                return (
                  <Table.Td key={id} fw={500}>
                    {value.toFixed(2)}
                  </Table.Td>
                );
              })}
            </Table.Tr>
            {sortedRows.map((row) => (
              <ManhoursTableRowView
                key={row.id}
                row={row}
                datePeriods={datePeriods}
              />
            ))}
          </Table.Tbody>
          {rows.length > 0 && (
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td>Sum</Table.Td>
                <Table.Td />
                <Table.Td />
                <Table.Td>
                  {formatHours(totalCtcHours, { decimals: 0 })}
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          )}
        </Table>
      </Table.ScrollContainer>
    </Paper>
  );
};
